import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, withModifiers as _withModifiers, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, vModelText as _vModelText, withDirectives as _withDirectives, Transition as _Transition, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "ach-head" }
const _hoisted_2 = {
  key: 0,
  class: "ach-chat-hinfo"
}
const _hoisted_3 = ["src", "alt"]
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { class: "ach-chat-hname-wrap" }
const _hoisted_6 = { class: "ach-chat-hname" }
const _hoisted_7 = { class: "ach-chat-hstatus" }
const _hoisted_8 = {
  key: 0,
  class: "ach-empty"
}
const _hoisted_9 = {
  key: 0,
  class: "ach-empty"
}
const _hoisted_10 = { class: "ach-bubble" }
const _hoisted_11 = { class: "ach-bubble-text" }
const _hoisted_12 = { class: "ach-bubble-ts" }
const _hoisted_13 = {
  key: 0,
  class: "ach-error"
}
const _hoisted_14 = ["disabled"]
const _hoisted_15 = ["disabled"]

import { ref, computed, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useAgentChatCp } from '@/composables/useAgentChatCp'
import { useChatRailMode } from '@/composables/useChatRailMode'
import { usePermission } from '@/composables/usePermission'
import { avatarColor, initials } from '@/composables/useHelpers'
import { AVAIL_OPTIONS } from '@/composables/useAvailability'
import { toMediaUrl } from '@/utils/media'
import { openInternalChatPopup } from '@/composables/useInternalChatPopup'

const MIN_W = 260
const MAX_W = 640

export default /*@__PURE__*/_defineComponent({
  __name: 'AgentChatDrawerCp',
  setup(__props) {

const store    = useStore()
const authUser = computed(() => store.getters.authUser)
const token    = computed<string>(() => authUser.value?.wootdash_token ?? '')
const myId     = computed<string>(() => authUser.value?.wootdash_agent_id ?? '')

const {
  activeAgentId, activeAgent, activeMessages,
  panelOpen, loadingMessages, sendError,
  loadAgents, loadUnread, closePanel, send,
  startRealtime, stopRealtime,
} = useAgentChatCp()

const { mode: railMode } = useChatRailMode()
const { can } = usePermission()
const canUseAgentChat = computed(() => can('agentChat.view'))

const draft      = ref('')
const sending    = ref(false)
const msgListEl  = ref<HTMLElement | null>(null)

// Sem a faixa de avatares, o trilho da direita volta pro chat de clientes assim que esta conversa fecha (e fecha se outra
// tela puxar o trilho pro chat de clientes).
watch(panelOpen, open => { if (!open && railMode.value === 'agent') railMode.value = 'client' })
watch(railMode, mode => { if (mode !== 'agent' && panelOpen.value) closePanel() })

/** Voltar: a lista de agentes agora é a janela Conversas. */
function backToAgents() {
  closePanel()
  openInternalChatPopup()
}

function statusColor(av: string): string {
  return AVAIL_OPTIONS.find(o => o.value === av)?.color ?? '#9ca3af'
}
function statusLabel(av: string): string {
  return AVAIL_OPTIONS.find(o => o.value === av)?.label ?? 'Offline'
}

function fmtTs(iso: string): string {
  const d = new Date(iso)
  return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
}

function scrollToBottom() {
  nextTick(() => {
    if (msgListEl.value) msgListEl.value.scrollTop = msgListEl.value.scrollHeight
  })
}

/* ── Resize — mesmo mecanismo do AgentChatDrawer.vue original, chave própria pra não
   colidir se algum dia os dois coexistirem (não deveriam, mas custa zero garantir) ── */
const panelWidth = ref(Math.max(MIN_W, Math.min(MAX_W, parseInt(localStorage.getItem('ach_cp_panel_w') || '320'))))

let _rx = false, _sx = 0, _sw = 0

function startResize(e: MouseEvent) {
  _rx = true; _sx = e.clientX; _sw = panelWidth.value
  document.addEventListener('mousemove', doResize)
  document.addEventListener('mouseup', stopResize)
  document.body.style.userSelect = 'none'
  document.body.style.cursor = 'col-resize'
}
function doResize(e: MouseEvent) {
  if (!_rx) return
  panelWidth.value = Math.max(MIN_W, Math.min(MAX_W, _sw + (_sx - e.clientX)))
}
function stopResize() {
  _rx = false
  document.removeEventListener('mousemove', doResize)
  document.removeEventListener('mouseup', stopResize)
  document.body.style.userSelect = ''
  document.body.style.cursor = ''
  localStorage.setItem('ach_cp_panel_w', String(panelWidth.value))
}

async function onSend() {
  const text = draft.value
  if (!text.trim() || activeAgentId.value == null) return
  draft.value = ''
  sending.value = true
  await send(token.value, activeAgentId.value, text)
  sending.value = false
  scrollToBottom()
}

watch(activeMessages, scrollToBottom)

// Carrega agentes/não-lidas e arma o realtime assim que o token, o id do agente logado
// E a permissão (agentChat.view) estiverem disponíveis.
watch(
  () => [token.value, myId.value, canUseAgentChat.value] as const,
  ([tok, id, allowed]) => {
    if (!tok || !id || !allowed) {
      if (!allowed) stopRealtime()
      return
    }
    loadAgents(tok, id)
    loadUnread(tok)
    startRealtime(tok, id)
  },
  { immediate: true },
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Transition, { name: "ach-panel" }, {
    default: _withCtx(() => [
      (_unref(panelOpen) && _unref(activeAgentId) != null && _unref(railMode) === 'agent' && canUseAgentChat.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "ach-panel",
            style: _normalizeStyle({ width: panelWidth.value + 'px' })
          }, [
            _createElementVNode("div", {
              class: "ach-resize-handle",
              onMousedown: _withModifiers(startResize, ["prevent"])
            }, null, 32),
            _createElementVNode("div", _hoisted_1, [
              _createElementVNode("button", {
                class: "ach-icon-btn",
                onClick: backToAgents,
                title: "Voltar pra lista de agentes (janela Conversas)"
              }, [...(_cache[3] || (_cache[3] = [
                _createElementVNode("svg", {
                  width: "13",
                  height: "13",
                  viewBox: "0 0 24 24",
                  fill: "none",
                  stroke: "currentColor",
                  "stroke-width": "3",
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round"
                }, [
                  _createElementVNode("polyline", { points: "15 18 9 12 15 6" })
                ], -1)
              ]))]),
              (_unref(activeAgent))
                ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                    _createElementVNode("div", {
                      class: "ach-chat-hav",
                      style: _normalizeStyle({ background: _unref(avatarColor)(_unref(activeAgent).name) })
                    }, [
                      (_unref(activeAgent).avatarUrl)
                        ? (_openBlock(), _createElementBlock("img", {
                            key: 0,
                            src: _unref(toMediaUrl)(_unref(activeAgent).avatarUrl) ?? undefined,
                            alt: _unref(activeAgent).name,
                            onError: _cache[0] || (_cache[0] = (e) => (e.target as HTMLImageElement).style.display = 'none')
                          }, null, 40, _hoisted_3))
                        : (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(_unref(initials)(_unref(activeAgent).name)), 1))
                    ], 4),
                    _createElementVNode("div", _hoisted_5, [
                      _createElementVNode("div", _hoisted_6, _toDisplayString(_unref(activeAgent).name), 1),
                      _createElementVNode("div", _hoisted_7, [
                        _createElementVNode("span", {
                          class: "ach-status-dot sm",
                          style: _normalizeStyle({ background: statusColor(_unref(activeAgent).availability) })
                        }, null, 4),
                        _createTextVNode(" " + _toDisplayString(statusLabel(_unref(activeAgent).availability)), 1)
                      ])
                    ])
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("button", {
                class: "ach-icon-btn",
                onClick: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(closePanel) && _unref(closePanel)(...args)))
              }, [...(_cache[4] || (_cache[4] = [
                _createElementVNode("svg", {
                  width: "12",
                  height: "12",
                  viewBox: "0 0 24 24",
                  fill: "none",
                  stroke: "currentColor",
                  "stroke-width": "3",
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round"
                }, [
                  _createElementVNode("line", {
                    x1: "18",
                    y1: "6",
                    x2: "6",
                    y2: "18"
                  }),
                  _createElementVNode("line", {
                    x1: "6",
                    y1: "6",
                    x2: "18",
                    y2: "18"
                  })
                ], -1)
              ]))])
            ]),
            _createElementVNode("div", {
              class: "ach-messages",
              ref_key: "msgListEl",
              ref: msgListEl
            }, [
              (_unref(loadingMessages))
                ? (_openBlock(), _createElementBlock("div", _hoisted_8, "Carregando…"))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    (!_unref(activeMessages).length)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [...(_cache[5] || (_cache[5] = [
                          _createElementVNode("p", null, "Nenhuma mensagem ainda — diga olá!", -1)
                        ]))]))
                      : _createCommentVNode("", true),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(activeMessages), (m) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: m.id,
                        class: _normalizeClass(["ach-msg-row", m.senderId === myId.value ? 'out' : 'inn'])
                      }, [
                        _createElementVNode("div", _hoisted_10, [
                          _createElementVNode("span", _hoisted_11, _toDisplayString(m.body), 1),
                          _createElementVNode("span", _hoisted_12, _toDisplayString(fmtTs(m.createdAt)), 1)
                        ])
                      ], 2))
                    }), 128))
                  ], 64))
            ], 512),
            (_unref(sendError))
              ? (_openBlock(), _createElementBlock("div", _hoisted_13, _toDisplayString(_unref(sendError)), 1))
              : _createCommentVNode("", true),
            _createElementVNode("form", {
              class: "ach-input-row",
              onSubmit: _withModifiers(onSend, ["prevent"])
            }, [
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((draft).value = $event)),
                class: "ach-input",
                placeholder: "Mensagem… (Enter envia)",
                disabled: sending.value
              }, null, 8, _hoisted_14), [
                [_vModelText, draft.value]
              ]),
              _createElementVNode("button", {
                type: "submit",
                class: "ach-send-btn",
                disabled: !draft.value.trim() || sending.value
              }, [...(_cache[6] || (_cache[6] = [
                _createElementVNode("svg", {
                  width: "16",
                  height: "16",
                  viewBox: "0 0 24 24",
                  fill: "none",
                  stroke: "currentColor",
                  "stroke-width": "2.2",
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round"
                }, [
                  _createElementVNode("line", {
                    x1: "22",
                    y1: "2",
                    x2: "11",
                    y2: "13"
                  }),
                  _createElementVNode("polygon", { points: "22 2 15 22 11 13 2 9 22 2" })
                ], -1)
              ]))], 8, _hoisted_15)
            ], 32)
          ], 4))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})